import { Lock, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type { ResultRow } from '../../reporting/arrowToRows';
import type { RelatedManifests } from '../../reporting/columnRefs';
import { humanizeHeader } from '../../reporting/formatValue';
import {
  additiveMeasureAliases,
  TABLE_ROW_LIMIT,
  WIDGET_FORM_LABELS,
  WIDGET_FORMS,
  widgetFormReasons,
} from '../../reporting/widgetForms';
import {
  createDashboardTab,
  createReportWidget,
  getDashboardTabWidgets,
  listDashboardTabs,
  setDashboardTabWidgets,
  updateDashboardTab,
  updateReportWidget,
} from '../../services/reportingApi';
import { useStore } from '../../store';
import type {
  DashboardTab,
  DatasetManifestEntry,
  ReportDefinition,
  ReportListItem,
  ReportWidget,
  ReportWidgetDisplay,
  WidgetForm,
} from '../../types/reporting';
import { ReportWidgetBody } from '../dashboard/ReportWidgetCard';

const NONE = '__none__';
const NEW_TAB = '__new__';
const TAB_NAME_MAX = 100;

interface CreateWidgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: ReportListItem;
  definition: ReportDefinition;
  dataset: DatasetManifestEntry;
  related: RelatedManifests;
  widget?: ReportWidget;
  // Edit mode only: the builder page for this widget's report.
  editReportHref?: string;
  // The report's current results, for a preview of the card.
  preview?: { columns: string[]; rows: ResultRow[] };
  onSaved: (widget: ReportWidget) => void;
}

const CreateWidgetDialog = ({
  open,
  onOpenChange,
  report,
  definition,
  dataset,
  related,
  widget,
  editReportHref,
  preview,
  onSaved,
}: CreateWidgetDialogProps) => {
  const showError = useStore(state => state.showError);
  const editing = !!widget;
  const reasons = useMemo(
    () => widgetFormReasons(definition, dataset, related),
    [definition, dataset, related]
  );
  const measures = useMemo(
    () => additiveMeasureAliases(definition),
    [definition]
  );
  const firstForm = WIDGET_FORMS.find(f => reasons[f] === null) ?? 'table';

  const [name, setName] = useState('');
  const [form, setForm] = useState<WidgetForm>(firstForm);
  const [measure, setMeasure] = useState('');
  const [subtext, setSubtext] = useState(NONE);
  const [rowLimit, setRowLimit] = useState<number>(TABLE_ROW_LIMIT.default);
  const [visibility, setVisibility] = useState<'private' | 'agency'>('private');
  const [tabChoice, setTabChoice] = useState(NONE);
  const [newTabName, setNewTabName] = useState('');
  const [tabs, setTabs] = useState<DashboardTab[]>([]);
  const [saving, setSaving] = useState(false);
  // Steps that already succeeded, so a retry after a failure does not repeat them.
  const createdWidget = useRef<ReportWidget | null>(null);
  const createdTab = useRef<DashboardTab | null>(null);

  // Reset the fields every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setName(widget?.name ?? report.name);
    setForm(widget?.form ?? firstForm);
    setMeasure(widget?.display.measure ?? measures[0] ?? '');
    setSubtext(widget?.display.subtext_measure ?? NONE);
    setRowLimit(widget?.display.row_limit ?? TABLE_ROW_LIMIT.default);
    setVisibility(widget?.visibility ?? 'private');
    setTabChoice(NONE);
    setNewTabName('');
    createdWidget.current = null;
    createdTab.current = null;
  }, [open]);

  useEffect(() => {
    if (!open || editing) return;
    let cancelled = false;
    listDashboardTabs()
      .then(list => !cancelled && setTabs(list))
      .catch(() => !cancelled && setTabs([]));
    return () => {
      cancelled = true;
    };
  }, [open, editing]);

  const buildDisplay = (): ReportWidgetDisplay => {
    if (form === 'number') {
      return subtext !== NONE
        ? { measure, subtext_measure: subtext }
        : { measure };
    }
    if (form === 'pie') return { measure };
    if (form === 'table') return { row_limit: rowLimit };
    return {};
  };

  const needsMeasure = form === 'number' || form === 'pie';
  const rowLimitValid =
    Number.isInteger(rowLimit) &&
    rowLimit >= TABLE_ROW_LIMIT.min &&
    rowLimit <= TABLE_ROW_LIMIT.max;
  const valid =
    name.trim().length > 0 &&
    reasons[form] === null &&
    (!needsMeasure || measure !== '') &&
    (form !== 'table' || rowLimitValid) &&
    (tabChoice !== NEW_TAB ||
      (newTabName.trim().length > 0 &&
        newTabName.trim().length <= TAB_NAME_MAX));

  const draft: ReportWidget = {
    id: widget?.id ?? 'preview',
    name: name.trim() || report.name,
    report_definition_id: report.id,
    system_report_key: report.system_key,
    form,
    display: buildDisplay(),
    visibility,
    created_by: widget?.created_by ?? '',
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fields = {
        name: name.trim(),
        form,
        display: buildDisplay(),
        visibility,
      };
      let saved: ReportWidget;
      if (widget) {
        saved = await updateReportWidget(widget.id, fields);
      } else {
        // Create the tab first, then the widget, then place it. Each step is
        // remembered, so a retry resumes where the last try stopped and saves
        // any edits made since then.
        if (tabChoice === NEW_TAB) {
          if (!createdTab.current) {
            createdTab.current = await createDashboardTab(newTabName.trim());
          } else if (createdTab.current.name !== newTabName.trim()) {
            createdTab.current = await updateDashboardTab(
              createdTab.current.id,
              { name: newTabName.trim() }
            );
          }
        }
        createdWidget.current = createdWidget.current
          ? await updateReportWidget(createdWidget.current.id, fields)
          : await createReportWidget({
              ...fields,
              report_definition_id: report.id,
              system_report_key: report.system_key,
            });
        saved = createdWidget.current;
        if (tabChoice !== NONE) {
          const tabId =
            tabChoice === NEW_TAB
              ? (createdTab.current as DashboardTab).id
              : tabChoice;
          const current = await getDashboardTabWidgets(tabId);
          const ids = current.map(i => i.widget_id);
          if (!ids.includes(saved.id)) {
            await setDashboardTabWidgets(tabId, [...ids, saved.id]);
          }
        }
      }
      onSaved(saved);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save widget', error);
      showError('Reports', 'Failed to save widget. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const measureSelect = (
    id: string,
    label: string,
    value: string,
    onChange: (v: string) => void,
    withNone = false
  ) => (
    <div className='space-y-1.5'>
      <Label id={`${id}-label`}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-labelledby={`${id}-label`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {withNone && <SelectItem value={NONE}>None</SelectItem>}
          {measures.map(alias => (
            <SelectItem key={alias} value={alias}>
              {humanizeHeader(alias)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={preview ? 'md:max-w-3xl' : undefined}>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit widget' : 'Create widget'}</DialogTitle>
          <DialogDescription className='sr-only'>
            Choose how this report shows on a dashboard.
          </DialogDescription>
          {editing && editReportHref ? (
            <Link to={editReportHref} className='w-fit text-sm underline'>
              Edit report
            </Link>
          ) : null}
        </DialogHeader>
        <div
          className={
            preview ? 'grid gap-4 md:grid-cols-[1fr_minmax(0,1fr)]' : undefined
          }
        >
          <div className='space-y-3'>
            <div className='space-y-1.5'>
              <Label htmlFor='widget-name'>Name</Label>
              <Input
                id='widget-name'
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Display as</Label>
              <RadioGroup
                value={form}
                onValueChange={v => setForm(v as WidgetForm)}
                aria-label='Display as'
              >
                {WIDGET_FORMS.map(f => (
                  <div key={f} className='flex flex-wrap items-center gap-2'>
                    <RadioGroupItem
                      value={f}
                      id={`widget-form-${f}`}
                      disabled={reasons[f] !== null}
                    />
                    <Label htmlFor={`widget-form-${f}`}>
                      {WIDGET_FORM_LABELS[f]}
                    </Label>
                    {reasons[f] && (
                      <span className='text-xs text-muted-foreground'>
                        {reasons[f]}
                      </span>
                    )}
                  </div>
                ))}
              </RadioGroup>
            </div>
            {needsMeasure &&
              measureSelect('widget-measure', 'Value', measure, setMeasure)}
            {form === 'number' &&
              measureSelect(
                'widget-subtext',
                'Secondary value',
                subtext,
                setSubtext,
                true
              )}
            {form === 'table' && (
              <div className='space-y-1.5'>
                <Label htmlFor='widget-rows'>Rows</Label>
                <Input
                  id='widget-rows'
                  type='number'
                  min={TABLE_ROW_LIMIT.min}
                  max={TABLE_ROW_LIMIT.max}
                  value={rowLimit}
                  onChange={e => setRowLimit(e.target.valueAsNumber)}
                />
              </div>
            )}
            <div className='space-y-1.5'>
              <Label>Who can open this</Label>
              <div
                className='inline-flex rounded-lg border border-border/80 bg-muted/40 p-1 shadow-sm'
                role='group'
                aria-label='Who can open this'
              >
                <Button
                  type='button'
                  size='sm'
                  variant={visibility === 'private' ? 'default' : 'ghost'}
                  onClick={() => setVisibility('private')}
                >
                  <Lock className='mr-2 h-4 w-4' />
                  Only me
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant={visibility === 'agency' ? 'default' : 'ghost'}
                  disabled={report.visibility === 'private'}
                  onClick={() => setVisibility('agency')}
                >
                  <Users className='mr-2 h-4 w-4' />
                  My agency
                </Button>
              </div>
            </div>
            {!editing && (
              <div className='space-y-1.5'>
                <Label>Add to dashboard</Label>
                <Select value={tabChoice} onValueChange={setTabChoice}>
                  <SelectTrigger aria-label='Add to dashboard'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>
                      Save to widget library only
                    </SelectItem>
                    {tabs.map(tab => (
                      <SelectItem key={tab.id} value={tab.id}>
                        {tab.name}
                      </SelectItem>
                    ))}
                    <SelectItem value={NEW_TAB}>New dashboard</SelectItem>
                  </SelectContent>
                </Select>
                {tabChoice === NEW_TAB && (
                  <>
                    <Label htmlFor='widget-new-tab'>New dashboard name</Label>
                    <Input
                      id='widget-new-tab'
                      value={newTabName}
                      maxLength={TAB_NAME_MAX}
                      onChange={e => setNewTabName(e.target.value)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          {preview ? (
            <div className='min-w-0 space-y-1.5'>
              <Label>Preview</Label>
              <div className='overflow-hidden rounded-xl border bg-card'>
                <p className='border-b px-4 py-2 text-sm font-semibold'>
                  {draft.name}
                </p>
                <div className='max-h-80 overflow-auto px-4 py-3'>
                  {reasons[form] === null ? (
                    <ReportWidgetBody
                      widget={draft}
                      definition={definition}
                      dataset={dataset}
                      related={related}
                      columns={preview.columns}
                      rows={preview.rows}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !valid}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateWidgetDialog;
